import React, { useState, useEffect } from 'react';
import { loadClients, updateClient } from '../utils/storage';

// Función auxiliar para generar un ID único para la tarea
const generateTaskId = (task) => {
  return `${task.clientId}-${task.meetingId}-${task.id}`;
};

// Función auxiliar para obtener la URL base
const getBaseUrl = () => {
  // En desarrollo
  if (window.location.hostname === 'localhost') {
    return `http://localhost:${window.location.port}`;
  }
  // En producción
  return window.location.protocol + '//' + window.location.host;
};

// Función auxiliar para abrir la tarea en una nueva ventana
const openTaskInNewWindow = (task) => {
  const taskId = generateTaskId(task);
  const baseUrl = getBaseUrl();
  const taskUrl = `${baseUrl}/?taskId=${taskId}`;
  window.open(taskUrl, '_blank', 'width=800,height=600');
};

const AllTasksSection = ({ onClose, currentUser }) => {
  const [allClients, setAllClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'completed'

  // Obtener todos los usuarios para el filtro de asignación
  const allUsers = [];
  if (allClients && allClients.length > 0) {
    const assignedToSet = new Set();
    allClients.forEach(client => {
      if (client.meetings && Array.isArray(client.meetings)) {
        client.meetings.forEach(meeting => {
          if (meeting && meeting.tasks && Array.isArray(meeting.tasks)) {
            meeting.tasks.forEach(task => {
              if (task && task.assignedTo) {
                assignedToSet.add(task.assignedTo);
              }
            });
          }
        });
      }
    });
    assignedToSet.forEach(user => allUsers.push(user));
  }

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const loadedClients = await loadClients();
        setAllClients(loadedClients);
        
        // Si es un usuario estándar, solo mostrar sus tareas asignadas
        if (currentUser.role === 'standard') {
          setFilterAssignedTo(currentUser.email);
        }
        
        processAndFilterTasks(loadedClients, currentUser.role === 'standard' ? currentUser.email : filterAssignedTo, filterStatus);
      } catch (error) {
        console.error('Error cargando clientes:', error);
        setAllClients([]);
      }
    };
    loadInitialData();
  }, [filterAssignedTo, filterStatus, currentUser]); // Dependencias de los filtros

  const processAndFilterTasks = (clientsToProcess, assignedToFilter, statusFilter) => {
    let collectedTasks = [];
    if (!Array.isArray(clientsToProcess)) return setTasks([]);

    clientsToProcess.forEach(client => {
      // Si el usuario es estándar, solo procesar clientes donde es el ejecutivo
      if (currentUser.role === 'standard' && client.salesExecutive !== currentUser.email) {
        return;
      }

      if (client.meetings && Array.isArray(client.meetings)) {
        client.meetings.forEach(meeting => {
          if (meeting && meeting.tasks && Array.isArray(meeting.tasks)) {
            meeting.tasks.forEach(task => {
              if (task) {
                collectedTasks.push({
                  ...task,
                  clientId: client.id,
                  clientName: client.name,
                  meetingDate: meeting.date,
                  meetingType: meeting.meetingType,
                  meetingId: meeting.id,
                  salesExecutive: client.salesExecutive // Agregar el ejecutivo de ventas a la tarea
                });
              }
            });
          }
        });
      }
    });

    // Aplicar filtros
    let filtered = collectedTasks;
    if (assignedToFilter) {
      filtered = filtered.filter(task => task.assignedTo === assignedToFilter);
    }
    if (statusFilter === 'pending') {
      filtered = filtered.filter(task => !task.completed);
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(task => task.completed);
    }

    // Ordenar tareas: pendientes primero, luego por fecha límite
    filtered.sort((a, b) => {
      const completedA = a.completed ? 1 : 0;
      const completedB = b.completed ? 1 : 0;
      if (completedA !== completedB) return completedA - completedB; // Pendientes antes que completadas

      const dateA = a.dueDate ? new Date(a.dueDate) : new Date(a.meetingDate);
      const dateB = b.dueDate ? new Date(b.dueDate) : new Date(b.meetingDate);
      return dateA - dateB;
    });

    setTasks(filtered);
  };

  const handleTaskCompletion = async (taskId, meetingId, clientId, isCompleted, comment) => {
    const updatedClients = allClients.map(c => {
      if (c.id === clientId) {
        const updatedMeetings = c.meetings.map(m => {
          if (m.id === meetingId) {
            const updatedTasks = m.tasks.map(task => 
              task.id === taskId ? { ...task, completed: isCompleted, completionComment: comment } : task
            );
            return { ...m, tasks: updatedTasks };
          }
          return m;
        });
        return { ...c, meetings: updatedMeetings };
      }
      return c;
    });

    try {
      const clientToUpdate = updatedClients.find(c => c.id === clientId);
      await updateClient(clientId, clientToUpdate);
      setAllClients(updatedClients);
      processAndFilterTasks(updatedClients, filterAssignedTo, filterStatus);
    } catch (error) {
      console.error('Error al actualizar tarea:', error);
      alert('Error al actualizar tarea. Por favor, intente de nuevo.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-[#0061A8]">Todas las Tareas</h2>
            <button 
              onClick={onClose}
              className="text-[#63666A] hover:text-[#0061A8]"
            >
              ✕
            </button>
          </div>

          {/* Filtros de Tareas */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {currentUser.role === 'admin' && (
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Asignado a</label>
                <select
                  value={filterAssignedTo}
                  onChange={(e) => setFilterAssignedTo(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                >
                  <option value="">Todos</option>
                  {allUsers.map(user => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#63666A]">Estado</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="all">Todas</option>
                <option value="pending">Pendientes</option>
                <option value="completed">Realizadas</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterAssignedTo('');
                  setFilterStatus('all');
                }}
                className="w-full bg-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>

          {tasks.length === 0 ? (
            <p className="text-[#63666A] text-sm">No hay tareas que coincidan con los filtros.</p>
          ) : (
            <div className="space-y-4">
              {tasks.map((task, index) => (
                <div 
                  key={task.id || index}
                  className={`bg-white shadow-md p-4 rounded-lg border-l-4 ${
                    task.completed ? 'border-[#A8D08D] bg-opacity-90' : 'border-[#0061A8]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${task.completed ? 'line-through text-[#63666A]' : 'text-[#0061A8]'}`}>
                        {task.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openTaskInNewWindow(task)}
                        className="text-[#0061A8] hover:text-opacity-80 p-1"
                        title="Ver detalles"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(
                          `Tarea: ${task.description}\nCliente: ${task.clientName}\nFecha Límite: ${
                            task.dueDate ? new Date(task.dueDate).toLocaleDateString('es-MX') : 'N/A'
                          }\nAsignado a: ${task.assignedTo || 'N/A'}\n\nVer detalles: ${getBaseUrl()}/?taskId=${generateTaskId(task)}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#25D366] hover:text-opacity-80 p-1"
                        title="Compartir por WhatsApp"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </a>
                      <input
                        type="checkbox"
                        checked={task.completed || false}
                        onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, e.target.checked, task.completionComment || '')}
                        className="form-checkbox h-5 w-5 text-[#A8D08D] rounded border-[#D1D3D4]"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-[#63666A] mt-2">
                    Cliente: {task.clientName} | Asignado a: {task.assignedTo || 'N/A'}
                    {currentUser.role === 'admin' && task.createdBy && (
                      <span className="ml-2 text-xs text-[#A8D08D]">
                        (Creado por: {task.createdBy})
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-[#63666A]">
                    Fecha Límite: {task.dueDate ? new Date(task.dueDate).toLocaleDateString('es-MX') : 'N/A'}
                  </p>
                  <p className="text-sm text-[#63666A]">
                    Origen: Junta del {new Date(task.meetingDate).toLocaleDateString('es-MX')} ({task.meetingType || 'N/A'})
                  </p>
                  {task.completed && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-[#63666A]">Comentario de finalización:</label>
                      <textarea
                        value={task.completionComment || ''}
                        onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, task.completed, e.target.value)}
                        rows="2"
                        className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 text-sm text-[#0061A8] focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                        placeholder="Agregar un comentario sobre la finalización de la tarea..."
                      ></textarea>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllTasksSection;
